import type { User } from '@milk/shared';
import { apiBase, authToken } from './config';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

export async function request<T>(method: string, path: string, body?: unknown, opts: { signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = authToken();
  if (token) headers.authorization = `Bearer ${token}`;
  let payload: string | undefined;
  if (body !== undefined) { payload = JSON.stringify(body); headers['content-type'] = 'application/json'; }
  let res: Response;
  try {
    res = await fetch(apiBase() + path, { method, headers, body: payload, credentials: token ? 'omit' : 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  }
  const text = await res.text();
  let data: any;
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? (typeof data === 'string' && data ? data : res.statusText), data?.code);
  return data as T;
}

const get = <T>(p: string) => request<T>('GET', p);
const post = <T>(p: string, b: unknown = {}) => request<T>('POST', p, b);

export interface SetupInfo { needsSetup: boolean; version: string; publicUrl: string }
export interface Session { user: User; token?: string }
export interface Invite { id: string; email: string | null; makeAdmin: boolean; note: string | null; createdAt: string; expiresAt: string; acceptedAt: string | null; acceptedBy: string | null; status: 'open' | 'accepted' | 'expired' }
export interface Passkey { id: string; name: string; createdAt: string; lastUsedAt: string | null }
export interface ApiToken { id: string; name: string; createdAt: string; lastUsedAt: string }

export const api = {
  setup: () => get<SetupInfo>('/api/v1/setup'),
  createAdmin: (b: { email: string; name: string; password: string; client?: string }) => post<Session>('/api/v1/auth/setup', b),
  login: (b: { email: string; password: string; client?: string }) => post<Session>('/api/v1/auth/login', b),
  signup: (b: { email: string; name: string; password: string; inviteToken: string; client?: string }) => post<Session>('/api/v1/auth/signup', b),
  invite: (token: string) => get<{ valid: boolean; email: string | null }>(`/api/v1/invites/${encodeURIComponent(token)}`),
  logout: () => post('/api/v1/auth/logout'),
  me: () => get<{ user: User }>('/api/v1/auth/me'),
  updateMe: (name: string) => request<{ user: User }>('PATCH', '/api/v1/auth/me', { name }),
  changePassword: (current: string, next: string) => post('/api/v1/auth/password', { current, next }),
  passkeys: () => get<Passkey[]>('/api/v1/auth/passkeys'),
  passkeyRegisterOptions: () => post<{ challengeId: string; options: any }>('/api/v1/auth/passkeys/register/options'),
  passkeyRegisterVerify: (challengeId: string, response: unknown, name: string) => post('/api/v1/auth/passkeys/register/verify', { challengeId, response, name }),
  deletePasskey: (id: string) => request('DELETE', `/api/v1/auth/passkeys/${encodeURIComponent(id)}`),
  passkeyLoginOptions: () => post<{ challengeId: string; options: any }>('/api/v1/auth/passkeys/login/options'),
  passkeyLoginVerify: (challengeId: string, response: unknown, client?: string) => post<Session>('/api/v1/auth/passkeys/login/verify', { challengeId, response, client }),
  tokens: () => get<ApiToken[]>('/api/v1/auth/tokens'),
  createToken: (name: string) => post<{ id: string; name: string; token: string }>('/api/v1/auth/tokens', { name }),
  deleteToken: (id: string) => request('DELETE', `/api/v1/auth/tokens/${id}`),
  invites: () => get<Invite[]>('/api/v1/admin/invites'),
  createInvite: (b: { email?: string | null; makeAdmin?: boolean }) => post<{ id: string; url: string; email: string | null; expiresAt: string }>('/api/v1/admin/invites', b),
  deleteInvite: (id: string) => request('DELETE', `/api/v1/admin/invites/${id}`),
  users: () => get<User[]>('/api/v1/admin/users'),
  setAdmin: (id: string, isAdmin: boolean) => request('PATCH', `/api/v1/admin/users/${id}`, { isAdmin }),
  deleteUser: (id: string) => request('DELETE', `/api/v1/admin/users/${id}`),
};
